import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronsUpDown,
  ChevronsDownUp,
  BookOpen,
  Loader2,
  Search,
  Pencil,
} from "lucide-react";
import { clsx } from "clsx";
import PublicHeader from "@/components/common/PublicHeader";
import { api } from "@/api/client";
import { useAuthStore } from "@/store/authSlice";

// ── Types ─────────────────────────────────────────────────────────────────────

type JenisPnbp = {
  id: string;
  kode: string;
  nama: string;
  deskripsi: string | null;
  urutan: number;
  doc_count: number;
};

type PnbpCluster = {
  id: string;
  title: string;
  content_html: string | null;
  urutan: number;
};

// ── Color palette (berputar per urutan) ───────────────────────────────────────

const COLORS = [
  {
    badge: "bg-emerald-600",
    text: "text-emerald-700",
    lightText: "text-emerald-600",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    activeLi:
      "border-l-2 border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold",
  },
  {
    badge: "bg-blue-600",
    text: "text-blue-700",
    lightText: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-200",
    activeLi:
      "border-l-2 border-blue-500 bg-blue-50 text-blue-700 font-semibold",
  },
  {
    badge: "bg-violet-600",
    text: "text-violet-700",
    lightText: "text-violet-600",
    bg: "bg-violet-50",
    border: "border-violet-200",
    activeLi:
      "border-l-2 border-violet-500 bg-violet-50 text-violet-700 font-semibold",
  },
  {
    badge: "bg-orange-500",
    text: "text-orange-700",
    lightText: "text-orange-600",
    bg: "bg-orange-50",
    border: "border-orange-200",
    activeLi:
      "border-l-2 border-orange-500 bg-orange-50 text-orange-700 font-semibold",
  },
  {
    badge: "bg-teal-600",
    text: "text-teal-700",
    lightText: "text-teal-600",
    bg: "bg-teal-50",
    border: "border-teal-200",
    activeLi:
      "border-l-2 border-teal-500 bg-teal-50 text-teal-700 font-semibold",
  },
  {
    badge: "bg-rose-600",
    text: "text-rose-700",
    lightText: "text-rose-600",
    bg: "bg-rose-50",
    border: "border-rose-200",
    activeLi:
      "border-l-2 border-rose-500 bg-rose-50 text-rose-700 font-semibold",
  },
];

// ── Accordion item ────────────────────────────────────────────────────────────

function JenisAccordionItem({
  jenis,
  idx,
  isOpen,
  onToggle,
  isAdmin,
}: {
  jenis: JenisPnbp;
  idx: number;
  isOpen: boolean;
  onToggle: () => void;
  isAdmin: boolean;
}) {
  const [selectedClusterId, setSelectedClusterId] = useState<string | null>(null);
  const c = COLORS[idx % COLORS.length];

  const { data: clusters = [], isLoading: clustersLoading } = useQuery<PnbpCluster[]>({
    queryKey: ["public", "pnbp-cluster", jenis.id],
    queryFn: () => api.get<PnbpCluster[]>(`/public/jenis-pnbp/${jenis.id}/clusters`),
    enabled: isOpen,
    staleTime: 10 * 60 * 1000,
  });

  const cluster = clusters.find((c) => c.id === selectedClusterId) ?? clusters[0] ?? null;

  return (
    <div
      id={`jenis-${jenis.id}`}
      className={clsx(
        "rounded-xl border bg-white shadow-sm overflow-hidden transition-all",
        isOpen ? c.border : "border-slate-200 hover:border-slate-300",
      )}
    >
      {/* ── Header row ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-4 px-5 py-4">
        {/* Nomor badge */}
        <div
          className={clsx(
            "h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-sm font-bold shadow-sm",
            c.badge,
          )}
        >
          {idx + 1}
        </div>

        {/* Title + desc — klik seluruh area untuk toggle */}
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 min-w-0 text-left"
        >
          <p
            className={clsx(
              "text-base font-bold leading-tight",
              isOpen ? c.text : "text-slate-900",
            )}
          >
            {jenis.nama}
          </p>
          {jenis.deskripsi && (
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed line-clamp-1">
              {jenis.deskripsi}
            </p>
          )}
        </button>

        {/* Disembunyikan sementara — bisa diaktifkan kembali bila diperlukan
        {isAdmin && (
          <Link
            to="/content/struktur-pnbp"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors flex-shrink-0"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Link>
        )}
        */}

        {/* Chevron */}
        <button
          type="button"
          onClick={onToggle}
          className="rounded-lg p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors flex-shrink-0"
        >
          {isOpen ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* ── Expanded: 3-column layout ───────────────────────────────── */}
      {isOpen && (
        <div
          className={clsx(
            "border-t grid grid-cols-1 lg:grid-cols-[220px_1fr]",
            c.border,
          )}
        >
          {/* LEFT: Cluster list */}
          <div className="border-r border-slate-100 bg-slate-50/70 py-2">
            <p className="px-4 pt-1 pb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Cluster
            </p>
            {clustersLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              </div>
            ) : clusters.length === 0 ? (
              <p className="px-4 py-3 text-xs text-slate-400 italic">Belum ada cluster.</p>
            ) : (
              <ul>
                {clusters.map((cat, ci) => {
                  const isActive = cluster?.id === cat.id;
                  return (
                    <li key={cat.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedClusterId(cat.id)}
                        className={clsx(
                          "flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm transition-colors",
                          isActive ? c.bg : "hover:bg-slate-50",
                        )}
                      >
                        {isActive ? (
                          <span
                            className={clsx(
                              "h-5 w-5 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-white",
                              c.badge,
                            )}
                          >
                            {ci + 1}
                          </span>
                        ) : (
                          <span className="w-5 flex-shrink-0 text-center text-xs text-slate-400 font-medium">
                            {ci + 1}
                          </span>
                        )}
                        <span
                          className={clsx(
                            "truncate text-sm",
                            isActive ? `${c.text} font-semibold` : "text-slate-600",
                          )}
                        >
                          {cat.title}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* MIDDLE: Cluster detail */}
          <div className="px-6 py-5 min-w-0 overflow-hidden">
            {clustersLoading ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Memuat konten...
              </div>
            ) : cluster ? (
              <>
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-200 flex-wrap">
                  <h3 className="text-sm font-bold text-slate-900 break-words min-w-0">{cluster.title}</h3>
                  <span
                    className={clsx(
                      "text-xs font-semibold px-2 py-0.5 rounded-full border",
                      c.bg,
                      c.text,
                      c.border,
                    )}
                  >
                    {jenis.kode}
                  </span>
                </div>
                {cluster.content_html ? (
                  <div
                    className="prose prose-sm max-w-none text-slate-700 break-words [&_h1]:text-base [&_h1]:font-bold [&_h1]:mb-1 [&_h2]:text-sm [&_h2]:font-bold [&_h2]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mb-0.5 [&_p]:text-sm [&_li]:text-sm"
                    dangerouslySetInnerHTML={{ __html: cluster.content_html }}
                  />
                ) : (
                  <p className="text-xs text-slate-400 italic">Belum ada konten untuk cluster ini.</p>
                )}
              </>
            ) : (
              <p className="text-xs text-slate-400 italic">Pilih cluster di kiri untuk membaca konten.</p>
            )}
          </div>

          {/* RIGHT: Action card — disembunyikan sementara, bisa diaktifkan kembali
              (kembalikan juga grid ke lg:grid-cols-[220px_1fr_220px] di atas)
          <div className="border-l border-slate-100 bg-slate-50/40 p-5 flex flex-col items-center gap-4 text-center min-w-0 overflow-hidden">
            {cluster ? (
              <>
                <div
                  className={clsx(
                    "flex h-14 w-14 items-center justify-center rounded-2xl border-2 flex-shrink-0",
                    c.bg, c.border,
                  )}
                >
                  <BookOpen className={clsx("h-7 w-7", c.lightText)} />
                </div>
                <div className="w-full min-w-0">
                  <p className="text-sm font-bold text-slate-800 leading-snug break-words line-clamp-3">
                    {cluster.title}
                  </p>
                  <p className="text-xs text-slate-500 mt-1 leading-tight break-words">
                    {jenis.nama}
                  </p>
                </div>
                <div className="w-full space-y-2">
                  <Link
                    to="/pnbp"
                    className="flex items-center justify-center gap-1.5 w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
                    style={{ backgroundColor: "#1B5189" }}
                  >
                    <BookOpen className="h-4 w-4" />
                    Baca Dokumen
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/content/struktur-pnbp"
                      className="flex items-center justify-center gap-1.5 w-full rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                    >
                      <Pencil className="h-4 w-4" />
                      Edit Konten
                    </Link>
                  )}
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Pilih cluster di kiri untuk membaca konten lainnya.
                </p>
              </>
            ) : (
              <p className="text-xs text-slate-400">
                Pilih cluster di kiri untuk membaca konten
              </p>
            )}
          </div>
          */}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PnbpKategoriPage() {
  const [searchParams] = useSearchParams();
  const targetNama = searchParams.get("nama");

  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.roles?.includes("ADMIN") ?? false;

  const { data: jenisList = [], isLoading } = useQuery<JenisPnbp[]>({
    queryKey: ["public", "jenis-pnbp", "stats"],
    queryFn: () => api.get<JenisPnbp[]>("/public/jenis-pnbp/stats"),
    staleTime: 10 * 60 * 1000,
  });

  // Auto-open item jika ada ?nama= di URL — cocokkan by nama (case-insensitive)
  useEffect(() => {
    if (!targetNama || jenisList.length === 0) return;
    const match = jenisList.find(
      (j) =>
        j.nama.toLowerCase() === decodeURIComponent(targetNama).toLowerCase(),
    );
    if (match) {
      setOpenIds(new Set([match.id]));
      setAllExpanded(false);
      setTimeout(() => {
        document
          .getElementById(`jenis-${match.id}`)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [targetNama, jenisList.length]);

  const toggle = (id: string) => {
    if (allExpanded) {
      // Saat semua terbuka, switch ke mode individual: tutup semua kecuali yang diklik
      const next = new Set(jenisList.map((j) => j.id));
      next.delete(id);
      setOpenIds(next);
      setAllExpanded(false);
    } else {
      setOpenIds((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    }
  };

  const handleExpandAll = () => {
    setAllExpanded(true);
    setOpenIds(new Set());
  };

  const handleCollapseAll = () => {
    setAllExpanded(false);
    setOpenIds(new Set());
  };

  const isOpen = (id: string) => allExpanded || openIds.has(id);

  const matchedJenis = targetNama
    ? jenisList.find(
        (j) =>
          j.nama.toLowerCase() ===
          decodeURIComponent(targetNama).toLowerCase(),
      )
    : null;

  const filteredJenisList = useMemo(() => {
    if (!searchQuery.trim()) return jenisList;
    const q = searchQuery.toLowerCase();
    return jenisList.filter(
      (j) =>
        j.nama.toLowerCase().includes(q) || j.kode.toLowerCase().includes(q),
    );
  }, [jenisList, searchQuery]);

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <PublicHeader />

      <div className="mx-auto max-w-5xl px-6 py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1 mb-6 text-xs text-slate-400">
          <Link to="/" className="hover:text-slate-600 transition-colors">
            Beranda
          </Link>
          <ChevronRight className="h-3 w-3 flex-shrink-0" />
          {matchedJenis ? (
            <>
              <Link
                to="/struktur"
                className="hover:text-slate-600 transition-colors"
              >
                Struktur PNBP
              </Link>
              <ChevronRight className="h-3 w-3 flex-shrink-0" />
              <Link
                to="/pnbp-objek"
                className="hover:text-slate-600 transition-colors"
              >
                Objek PNBP
              </Link>
              <ChevronRight className="h-3 w-3 flex-shrink-0" />
              <span className="text-slate-700 font-semibold truncate">
                {matchedJenis.nama}
              </span>
            </>
          ) : (
            <>
              <Link
                to="/struktur"
                className="hover:text-slate-600 transition-colors"
              >
                Struktur PNBP
              </Link>
              <ChevronRight className="h-3 w-3 flex-shrink-0" />
              <span className="text-slate-700 font-semibold">Objek PNBP</span>
            </>
          )}
        </nav>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Struktur PNBP</h1>
            <p className="mt-1.5 text-sm text-slate-500 max-w-xl">
              Pilih objek utama untuk melihat struktur pengelolaan dan definisi
              PNBP.
            </p>
          </div>

          {/* Buka / Tutup Semua (toggle) */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={allExpanded ? handleCollapseAll : handleExpandAll}
              className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
            >
              {allExpanded ? (
                <ChevronsDownUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronsUpDown className="h-3.5 w-3.5" />
              )}
              <span className="hidden sm:inline">
                {allExpanded ? "Tutup Semua" : "Buka Semua"}
              </span>
            </button>
          </div>
        </div>

        {/* Search bar */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cari kategori PNBP, kementerian, atau entitas..."
            className="w-full rounded-xl border border-slate-200 bg-white pl-11 pr-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
          />
        </div>

        {/* Accordion list */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20 gap-3 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Memuat Objek PNBP…</span>
          </div>
        ) : filteredJenisList.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <Search className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-sm">
              {searchQuery ? `Tidak ada hasil untuk "${searchQuery}"` : "Belum ada Objek PNBP tersedia."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredJenisList.map((jenis, idx) => (
              <JenisAccordionItem
                key={jenis.id}
                jenis={jenis}
                idx={idx}
                isOpen={isOpen(jenis.id)}
                onToggle={() => toggle(jenis.id)}
                isAdmin={isAdmin}
              />
            ))}
          </div>
        )}

        {/* Footer note */}
        {!isLoading && jenisList.length > 0 && (
          <p className="mt-8 text-center text-xs text-slate-400">
            Objek berdasarkan Pasal 3 UU No. 9 Tahun 2018 tentang PNBP.
          </p>
        )}
      </div>
    </div>
  );
}
